package fr.manyverse.app.presentation.dice3d

import fr.manyverse.app.domain.model.DieType

/**
 * Un dé physique à animer : maillage et étiquettes des faces. **Aucune valeur cible** — le dé
 * roule librement et c'est la face sur laquelle il se couche qui donne le résultat, comme un
 * vrai dé. Un d100 lancé se décompose en deux d10 (dizaines + unités) ; ses moitiés
 * n'acceptent pas d'image sur le 1 (aucune des deux ne porte à elle seule le résultat 1 :
 * « 00 » + « 1 » n'est un échec critique que combiné).
 */
class DisplayDie(
    val mesh: PolyhedronMesh,
    val labels: Map<Int, String>,
    val allowOneGlyph: Boolean
)

/** Dés à animer pour [count] lancers : un par lancer, sauf le d100 qui en demande deux. */
fun displayDiceFor(type: DieType, count: Int): List<DisplayDie> {
    val mesh = DiceMeshes.meshFor(type)
    return if (type == DieType.D100) {
        List(count) {
            listOf(
                DisplayDie(mesh, TENS_LABELS, allowOneGlyph = false),
                DisplayDie(mesh, UNITS_LABELS, allowOneGlyph = false)
            )
        }.flatten()
    } else {
        val labels = standardLabels(DiceMeshes.sidesFor(type))
        List(count) { DisplayDie(mesh, labels, allowOneGlyph = true) }
    }
}

/**
 * Résultats du lancer, lus sur les faces où les dés se sont couchés. Le dé des dizaines porte
 * « 00 »..« 90 » et celui des unités « 1 »..« 10 » : leur somme se lit directement
 * (00 + 1 = 1, 90 + 10 = 100), sans cas particulier.
 */
fun readRollResults(type: DieType, landedValues: List<Int>): List<Int> =
    if (type == DieType.D100) {
        landedValues.chunked(2).filter { it.size == 2 }.map { (tens, units) -> (tens - 1) * 10 + units }
    } else {
        landedValues
    }

/** Lancer auquel appartient le dé d'indice [dieIndex] : les d100 vont par paires. */
fun rollIndexForDie(type: DieType, dieIndex: Int): Int =
    if (type == DieType.D100) dieIndex / 2 else dieIndex

/**
 * Étiquettes 1..sides. Le texte atterrit à un angle quelconque (comme un vrai dé) : dès
 * qu'un dé porte un 6, « 6 » et « 9 » reçoivent un point de désambiguïsation, sinon un
 * 6 retombé à l'envers se lit 9.
 */
internal fun standardLabels(sides: Int): Map<Int, String> = (1..sides).associateWith { value ->
    if (sides >= 6 && (value == 6 || value == 9)) "$value." else value.toString()
}

/**
 * Dé des dizaines : faces « 00 » à « 90 » (valeur de face v → (v−1)·10). « 60 » et « 90 »
 * portent le point de désambiguïsation : retombés à l'envers, ils se lisent « 09 » et « 06 ».
 */
internal val TENS_LABELS: Map<Int, String> = (1..10).associateWith { value ->
    val tens = "${(value - 1) * 10}".padStart(2, '0')
    if (tens.any { it == '6' || it == '9' }) "$tens." else tens
}

/** Dé des unités : faces « 1 » à « 10 », avec « 6. »/« 9. » pour lever l'ambiguïté. */
internal val UNITS_LABELS: Map<Int, String> = standardLabels(10)
